import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'i18n';

import { TYPE_TO_READABLE_STRING_MAP, CUBOH_MENU_PARENT_MAP } from 'constants/menuManagement';
import { useMenuManagement } from 'hooks/useMenuManagement';
import { toAnalyticsEntityType, trackMenuEntityAction } from 'helpers/menuAnalytics';
import {
  useHandleSuccessNotification,
  useHandleErrorNotification,
} from 'components/MenuManagement/MenuEdit/hooks/useMMNotifications';
import { formatNotificationTimestamp } from 'helpers/format';

import ConfirmationModal from 'components/Modals/ConfirmationModal/ConfirmationModal';

const NS = 'menuManagementActions';

export default function DeleteMenuItemButton({
  record,
  group,
  menuId,
  deleteModalOpened,
  deleteModalHandlers,
}) {
  const { t } = useTranslation(NS);
  const { updateCubohMenu } = useMenuManagement();
  const handleSuccessNotification = useHandleSuccessNotification();
  const handleErrorNotification = useHandleErrorNotification();

  if (!record) return null;

  const menuType = TYPE_TO_READABLE_STRING_MAP[record.itemType];

  const parentType = CUBOH_MENU_PARENT_MAP[record.itemType] ?? '';

  const modalMessage = (() => {
    if (menuType === TYPE_TO_READABLE_STRING_MAP.cuboh_menu) {
      return t('DeleteMenuItemButton.menuDeleteWarningNoRemovePublished');
    }
    if (menuType === TYPE_TO_READABLE_STRING_MAP.cuboh_category) {
      return t('DeleteMenuItemButton.deleteCategoryConfirmation');
    }
    if (menuType === TYPE_TO_READABLE_STRING_MAP.cuboh_main_item) {
      return t('DeleteMenuItemButton.deleteItemConfirmation');
    }
    const firstParent = TYPE_TO_READABLE_STRING_MAP[parentType[0]?.toLowerCase()];
    const secondParent = parentType.length > 1 ? TYPE_TO_READABLE_STRING_MAP[parentType[1]] : '';
    const editParentText = secondParent
      ? t('DeleteMenuItemButton.editParentDouble', { firstParent, secondParent })
      : t('DeleteMenuItemButton.editParentSingle', { firstParent });
    const parents = secondParent
      ? `${firstParent.toLowerCase()} or ${secondParent.toLowerCase()}`
      : firstParent.toLowerCase();
    return t('DeleteMenuItemButton.deleteFromMenuInstruction', {
      itemType: menuType.toLowerCase(),
      parents,
      editParentText,
    });
  })();

  const handleDelete = async () => {
    const readableItemType = TYPE_TO_READABLE_STRING_MAP[record?.itemType].toLowerCase();

    try {
      await updateCubohMenu({
        group,
        id: record.key,
        data: { ...record, isDeleted: true },
      });

      trackMenuEntityAction({
        action: 'delete',
        entityType: toAnalyticsEntityType(record?.itemType),
        menuId,
        entityId: record?.uuid,
      });

      deleteModalHandlers.close();

      handleSuccessNotification({
        parentUuid: record?.parentUuid,
        customMessage: t('DeleteMenuItemButton.deleteSuccess', {
          itemType: readableItemType,
          timestamp: formatNotificationTimestamp(),
        }),
      });
    } catch (e) {
      handleErrorNotification({
        customMessage: t('DeleteMenuItemButton.deleteError', { itemType: readableItemType }),
      });
    }
  };

  return (
    <ConfirmationModal
      maskTransitionName={null}
      title={t('DeleteMenuItemButton.modalTitle', { menuType })}
      message={modalMessage}
      onConfirm={handleDelete}
      opened={deleteModalOpened}
      close={deleteModalHandlers.close}
    >
      {modalMessage}
    </ConfirmationModal>
  );
}

DeleteMenuItemButton.propTypes = {
  deleteModalOpened: PropTypes.bool,
  deleteModalHandlers: PropTypes.shape({ close: PropTypes.func }),
  group: PropTypes.string.isRequired,
  menuId: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  record: PropTypes.object,
};
